import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { clsx } from '@wego/alexis/helpers/clsx';

import HotelAmenityIcon from '@components/hotels/HotelAmenityIcon';

import { notUndefinedAndNull } from '@helpers/array';

import { getTranslationsState } from '@redux/selectors';

import { Hotel } from '@wegoTypes/hotels/hotel';
import { HotelCity } from '@wegoTypes/hotels/hotelCity';
import { Translations } from '@wegoTypes/translations';

import styles from './HotelDescription.module.scss';

export interface HotelDescriptionProps {
  className?: string;
  hotel: Hotel;
}

const HotelDescription: React.FC<HotelDescriptionProps> = ({ className, hotel }): JSX.Element => {
  const translations: Translations = useSelector(getTranslationsState);

  const descriptionDivRef = useRef<HTMLDivElement>(null);

  const [hasMoreDescription, setHasMoreDescription] = useState<boolean>(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState<boolean>(false);

  useEffect(() => {
    if (!!descriptionDivRef) {
      if (descriptionDivRef.current!.clientHeight > 8 * 24) {
        setHasMoreDescription(true);
      }
    }
  }, [descriptionDivRef]);

  const formatAddress = (hotel: Hotel) => {
    const { address, district, city = {} as HotelCity, postalCode } = hotel;

    const cityName = city.name !== city.countryName ? city.name : undefined;

    const countryAndPostal =
      city.countryName && postalCode ? `${city.countryName} ${postalCode}` : undefined;

    return [address?.trim(), district?.name, cityName, countryAndPostal]
      .filter((item) => notUndefinedAndNull(item) && item.trim() !== '')
      .join(', ');
  };

  return (
    <div className={clsx(styles.container, className)}>
      <div className={styles.descriptionAndHotelInformationContainer}>
        <div className={styles.descriptionContainer}>
          <div
            ref={descriptionDivRef}
            data-testid='hotel-description'
            className={clsx(
              styles.description,
              hasMoreDescription && !isDescriptionExpanded && styles.notExpanded,
            )}
          >
            {hotel.description}
          </div>

          {hasMoreDescription && !isDescriptionExpanded ? (
            <div
              data-testid='show-more-cta'
              className={styles.showAllLabel}
              onClick={() => setIsDescriptionExpanded(true)}
            >
              {translations.show_more}
            </div>
          ) : null}

          {hotel.licenseNumbers ? (
            <div className={styles.licenseNumbersContainer}>
              <h6 className={styles.licenseNumberTitle}>{translations.license_number} :</h6>
              <p className={styles.licenseNumber}>
                {hotel.licenseNumbers.map((license, idx, array) => (
                  <React.Fragment key={license}>
                    <span>{license}</span>
                    {idx !== array.length - 1 && <span> · </span>}
                  </React.Fragment>
                ))}
              </p>
            </div>
          ) : null}
        </div>

        <div className={styles.informationContainer}>
          {/* Address */}
          <div className={styles.information}>
            <div className={styles.hotelInformationHeading}>
              <i className={clsx(styles.hotelInformationIcon, styles['location-grey'])}></i>
              <div className={styles.label}>{translations.act_web_Address}</div>
            </div>
            <p data-testid='hotel-address' className={styles.content}>
              {formatAddress(hotel)}
            </p>
          </div>

          {/* Contact */}
          <div className={styles.information}>
            <div className={styles.hotelInformationHeading}>
              <i className={clsx(styles.hotelInformationIcon, styles['phone-grey'])}></i>
              <div className={styles.label}>{translations.contact}</div>
            </div>
            <p className={styles.content}>{hotel.phone1}</p>
          </div>

          {/* Check in time */}
          <div className={styles.information}>
            <div className={styles.hotelInformationHeading}>
              <i className={clsx(styles.hotelInformationIcon, styles['check-in-grey'])}></i>
              <div className={styles.label}>{translations.checkin_time}</div>
            </div>
            <p className={styles.content}>{hotel.checkIn}</p>
          </div>

          {/* Check out time */}
          <div className={styles.information}>
            <div className={styles.hotelInformationHeading}>
              <i className={clsx(styles.hotelInformationIcon, styles['check-out-grey'])}></i>
              <div className={styles.label}>{translations.checkout_time}</div>
            </div>
            <p className={styles.content}>{hotel.checkOut}</p>
          </div>
        </div>
      </div>

      {!!hotel.amenities && hotel.amenities.length > 0 ? (
        <div data-testid='hotel-amenities' className={styles.amenities}>
          <div className={styles.label}>{translations.amenities}</div>

          <div className={styles.amenitiesWrapper}>
            {hotel.amenities.map((amenity: { id: number; name: string }) => {
              return (
                <div key={amenity.id} className={styles.amenity}>
                  <HotelAmenityIcon amenityId={amenity.id} className={styles.hotelAmenitiesIcon} />
                  <span>{amenity.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default HotelDescription;
